import React, { useMemo, useState } from 'react';
import {
    Layout, Menu, theme, Button, Space,
    Card, Row, Col, Steps, Alert, Avatar,
    ConfigProvider, Typography, Timeline, Segmented, Tabs
} from 'antd';
import {
    UserOutlined,
    InfoCircleFilled,
    CheckCircleFilled,
    ExclamationCircleFilled,
    CloseCircleFilled,
    DesktopOutlined,
    TabletOutlined,
    MobileOutlined,
    MenuOutlined
} from '@ant-design/icons';

// Import Component ที่แยกออกไป
import PreviewLanding from './ThemeLanding';

const { Header, Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;

// --- Helper Functions ---
const getAlgorithm = (algo: string | string[]): any => {
    if (!algo) return theme.defaultAlgorithm;
    if (Array.isArray(algo)) {
        return algo.map(a => {
            if (a === 'dark') return theme.darkAlgorithm;
            if (a === 'compact') return theme.compactAlgorithm;
            return theme.defaultAlgorithm;
        });
    }
    if (algo === 'dark') return theme.darkAlgorithm;
    if (algo === 'compact') return theme.compactAlgorithm;
    return theme.defaultAlgorithm;
};

// ==========================================
// Main Component
// ==========================================
const ThemeLivePreview = ({ themeConfig }: { themeConfig: any }) => {
    const { token: parentToken } = theme.useToken();
    const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
    const [activeTab, setActiveTab] = useState('landing');

    const safeThemeConfig = useMemo(() => {
        if (!themeConfig) return { algorithm: theme.defaultAlgorithm };
        const newConfig = {
            ...themeConfig,
            algorithm: getAlgorithm(themeConfig.algorithm),
        };
        if (newConfig.components) {
            const cleanComponents = { ...newConfig.components };
            delete cleanComponents.Button;
            delete cleanComponents.Select;
            newConfig.components = cleanComponents;
        }
        return newConfig;
    }, [themeConfig]);

    const getContainerStyle = () => {
        const baseStyle: React.CSSProperties = {
            transition: 'all 0.3s ease',
            background: '#fff',
            margin: '0 auto',
            overflow: 'hidden',
        };

        if (viewMode === 'mobile') {
            return {
                ...baseStyle,
                width: '375px',
                height: '750px',
                border: `12px solid ${parentToken.colorBorderSecondary}`,
                borderRadius: 40,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                position: 'relative' as 'relative',
            };
        } else if (viewMode === 'tablet') {
            return {
                ...baseStyle,
                width: '768px',
                height: '800px',
                border: `1px solid ${parentToken.colorBorderSecondary}`,
                borderRadius: 8,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            };
        } else {
            return {
                ...baseStyle,
                width: '100%',
                height: '800px',
                border: `1px solid ${parentToken.colorBorderSecondary}`,
                borderRadius: 8,
            };
        }
    };

    const itemTabs = [
        { key: 'dashboard', label: 'Dashboard App', hidden: true },
        { key: 'landing', label: 'Hotel Booking (Landing)', hidden: false }
    ].filter(item => !item.hidden).map((item) => {
        return {
            key: item.key,
            label: item.label
        }
    })

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <ConfigProvider theme={{
                    algorithm: theme.defaultAlgorithm,
                    components: {
                        Tabs: {
                            colorBgContainer: "white",
                            itemColor: "black"
                        }
                    },
                }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        type="card"
                        size="small"
                        style={{ marginBottom: 0 }}
                        items={itemTabs}
                    />
                </ConfigProvider>
                <Segmented
                    options={[
                        { value: 'desktop', icon: <DesktopOutlined />, label: 'Desktop' },
                        { value: 'tablet', icon: <TabletOutlined />, label: 'Tablet' },
                        { value: 'mobile', icon: <MobileOutlined />, label: 'Mobile' },
                    ]}
                    value={viewMode}
                    onChange={(val) => setViewMode(val as any)}
                />
            </div>

            {/* Container */}
            <div style={getContainerStyle()}>
                {viewMode === 'mobile' && (
                    <div style={{
                        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                        width: 120, height: 24, background: parentToken.colorBorderSecondary,
                        borderBottomLeftRadius: 12, borderBottomRightRadius: 12, zIndex: 100
                    }} />
                )}

                <ConfigProvider theme={safeThemeConfig}>
                    {activeTab === 'dashboard' ? (
                        <PreviewDashboard viewMode={viewMode} />
                    ) : (
                        // เรียกใช้ Component ที่แยกออกมา
                        <PreviewLanding viewMode={viewMode} />
                    )}
                </ConfigProvider>
            </div>
        </div>
    );
};

// ==========================================
// Dashboard View (Internal Component)
// ==========================================
const PreviewDashboard = ({ viewMode }: { viewMode: string }) => {
    const { token } = theme.useToken();
    const isMobile = viewMode === 'mobile';
    const isTablet = viewMode === 'tablet';

    const ColorSection = ({ title, colorType, icon, colorToken }: any) => (
        <Card
            size="small"
            title={<Space>{icon}<Text strong style={{ color: colorToken }}>{title}</Text></Space>}
            style={{ height: '100%', borderColor: token.colorBorderSecondary }}
            bodyStyle={{ padding: '12px' }}
        >
            <Space orientation="vertical" style={{ width: '100%' }} size="small">
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button type="primary" size="small" style={{ backgroundColor: colorToken, borderColor: colorToken, boxShadow: "none", flex: 1 }}>Solid</Button>
                    <Button size="small" style={{ color: colorToken, borderColor: colorToken, flex: 1 }}>Outline</Button>
                </div>
                <Alert title="Status" type={colorType} showIcon style={{ padding: '4px 8px', fontSize: 12 }} />
            </Space>
        </Card>
    );

    return (
        <Layout style={{ height: '100%', overflow: 'hidden' }}>
            <Header style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: isMobile ? '0 16px' : '0 24px', height: isMobile ? 56 : 64,
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                marginTop: isMobile ? 20 : 0, transition: 'all 0.3s'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    {isMobile && <MenuOutlined style={{ color: token.colorTextBase }} />}
                    <div style={{ fontSize: 18, fontWeight: 'bold' }}>ADMIN</div>
                </div>
                {!isMobile && (
                    <Menu
                        mode="horizontal" defaultSelectedKeys={['1']}
                        items={['Dashboard', 'Users', 'Settings'].map((key, index) => ({ key: String(index + 1), label: key }))}
                        style={{ flex: 1, borderBottom: 'none', background: 'transparent', marginLeft: 40 }}
                    />
                )}
                <Avatar size={isMobile ? "small" : "default"} style={{ backgroundColor: token.colorPrimary }} icon={<UserOutlined />} />
            </Header>
            <Layout style={{ background: 'transparent', overflow: 'hidden' }}>
                {!isMobile && (
                    <Sider width={isTablet ? 60 : 200} collapsed={isTablet} style={{ background: 'transparent', borderRight: `1px solid ${token.colorBorderSecondary}` }}>
                        <Menu mode="inline" defaultSelectedKeys={['1']} style={{ height: '100%', borderRight: 0, background: 'transparent' }} items={[{ key: '1', icon: <UserOutlined />, label: 'Overview' }, { key: '2', icon: <InfoCircleFilled />, label: 'Details' }]} />
                    </Sider>
                )}
                <Layout style={{ padding: isMobile ? 12 : 24, background: 'transparent' }}>
                    <Content style={{ overflowY: 'auto', height: '100%', scrollbarWidth: isMobile ? 'none' : 'thin' }}>
                        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
                            <Card title="Typography" size="small" variant='borderless' style={{ background: token.colorBgContainer }}>
                                <Paragraph style={{ marginBottom: 0 }}>Base Text: <Text strong>{token.colorTextBase}</Text></Paragraph>
                                <Paragraph type="secondary" style={{ marginBottom: 0 }}>Secondary text preview.</Paragraph>
                            </Card>
                            <div>
                                <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>System Colors</Text>
                                <Row gutter={[12, 12]}>
                                    <Col span={isMobile ? 12 : 6}><ColorSection title="Info" colorType="info" colorToken={token.colorInfo} icon={<InfoCircleFilled style={{ color: token.colorInfo }} />} /></Col>
                                    <Col span={isMobile ? 12 : 6}><ColorSection title="Success" colorType="success" colorToken={token.colorSuccess} icon={<CheckCircleFilled style={{ color: token.colorSuccess }} />} /></Col>
                                    <Col span={isMobile ? 12 : 6}><ColorSection title="Warning" colorType="warning" colorToken={token.colorWarning} icon={<ExclamationCircleFilled style={{ color: token.colorWarning }} />} /></Col>
                                    <Col span={isMobile ? 12 : 6}><ColorSection title="Error" colorType="error" colorToken={token.colorError} icon={<CloseCircleFilled style={{ color: token.colorError }} />} /></Col>
                                </Row>
                            </div>
                            <Card title="Interaction" size="small" variant='borderless'>
                                <Row gutter={24}>
                                    <Col span={isMobile ? 24 : 12}><Steps orientation={isMobile ? 'vertical' : 'horizontal'} current={1} size="small" items={[{ title: 'Login', status: 'finish' }, { title: 'Verify', status: 'process' }, { title: 'Done', status: 'wait' }]} /></Col>
                                    {!isMobile && (<Col span={12}><Timeline items={[{ color: 'green', children: 'Created' }, { color: token.colorPrimary, children: 'Updated' }]} /></Col>)}
                                </Row>
                            </Card>
                        </Space>
                    </Content>
                </Layout>
            </Layout>
        </Layout>
    );
};

export default ThemeLivePreview;